<template>
    <IonPage :style="{ backgroundColor: screen === 'homepage' ? backgroundColour : null }">
        <div
            class="flex flex-col h-screen"
            :class="{ 'pt-(--beyond-safe-area-inset-top)': !isTransparentStatusBar || screen !== 'homepage' }"
        >
            <div
                v-if="!isTransparentStatusBar && screen === 'homepage'"
                class="absolute top-0 inset-x-0 h-(--beyond-safe-area-inset-top)"
                :style="{ backgroundColor: version.primary_color }"
            />
            <div
                ref="dashboardContainer"
                class="relative flex-1 overflow-auto"
                :style="{ backgroundColor: screen === 'homepage' ? backgroundColour : null }"
            >
                <KeepAlive>
                    <component
                        :is="activeScreen"
                        :dashboard="dashboard"
                    />
                </KeepAlive>
            </div>

            <DraftVersionBar ref="draftVersionBar" />

            <BottomBar />
        </div>

        <StatusIcons />
    </IonPage>
</template>

<script setup>
    import { useElementSize } from '@vueuse/core'
    import { analytics, mock, statusBar } from '@/services'

    import Homepage from '@/components/dashboard/Homepage.vue'
    import More from '@/components/classic/More.vue'
    import Notifications from '@/components/classic/Notifications.vue'
    import Identity from '@/components/classic/Identity.vue'
    import { useTourHelpers } from '@/composables/tourHelpers'
    import StatusIcons from '@/views/shared/StatusIcons.vue'
    import { events } from '@/composables/events'

    const router = useIonRouter()
    const dashboardContainer = ref()
    const { height: dashboardHeight } = useElementSize(dashboardContainer)

    provide('dashboardHeight', dashboardHeight)

    const dashboardStore = useDashboardStore()
    const versionStore = useVersionStore()
    const badgeStore = useBadgeStore()
    const identityStore = useIdentityStore()
    const moreStore = useMoreStore()
    const notificationsStore = useNotificationsStore()

    const pushNotifications = usePushNotifications()
    const tourHelpers = useTourHelpers()

    const { dashboard, screen } = storeToRefs(dashboardStore)
    const { version } = storeToRefs(versionStore)

    const hasReadItems = ref(false)
    const draftVersionBar = useTemplateRef('draftVersionBar')

    const handleReadItem = () => {
        hasReadItems.value = true
    }

    const isTransparentStatusBar = computed(() => {
        return get(dashboard.value, 'data.transparent_status_bar')
    })

    const badgeRefreshTimeout = ref(null)

    const scheduleBadgeRefresh = () => {
        console.log('Scheduling badge refresh')
        if (badgeRefreshTimeout.value) {
            clearTimeout(badgeRefreshTimeout.value)
        }
         
        badgeRefreshTimeout.value = setTimeout(badgeRefresh, 5 * 60 * 1000)
    }

    const badgeRefresh = () => {
        console.log('Refreshing dashboard badges')
        badgeStore.refreshAll()
        scheduleBadgeRefresh()
    }

    const activeScreen = computed(() => {
        switch (screen.value) {
            case 'more':
                return More
            case 'notifications':
                return Notifications
            case 'identity':
                return Identity
            case 'dashboard':
            default:
                return Homepage
        }
    })

    const backgroundColour = computed(() => {
        if (dashboard.value.data.background_color) {
            return dashboard.value.data.background_color
        }
        return version.value.background_color
    })

    const refreshInterface = () => {
        badgeStore.refreshAuthFailures()
        dashboardStore.load()
        moreStore.load()
        identityStore.load()
        notificationsStore.fetch()
    }

    watch(screen, (val) => {
        if (val === 'dashboard' || val === 'homepage') {
            draftVersionBar.value.reset()
        }
    })

    onMounted(() => {
        scheduleBadgeRefresh()
        events.on('read', handleReadItem)
        events.on('refresh-interface', refreshInterface)

        analytics.setScreenName({ screenName: 'Home' })

        // If mocking set the screen accordingly
        if (mock.mockingMore()) {
            screen.value = 'more'
        }

        if (mock.mockingNotifications()) {
            screen.value = 'notifications'
        }
    })

    onUnmounted(() => {
        if (badgeRefreshTimeout.value) {
            clearTimeout(badgeRefreshTimeout.value)
        }
        events.off('read', handleReadItem)
        events.off('refresh-interface', refreshInterface)
    })

    onIonViewWillEnter(async () => {
        if (screen.value === 'homepage') {
            // Check primary color is light or dark and change the status bar
            statusBar.setTheme(version.value.primary_color)
            draftVersionBar.value.reset()
        } else {
            statusBar.setTheme()
        }

        // Check if there have been any items read
        if (hasReadItems.value) {
            hasReadItems.value = false
            badgeRefresh()
        }

        // Launch the tour
        if (await tourHelpers.launchTour(router)) {
            // Prevent further execution
            return
        }

        // Remind the user to enable push notifications
        pushNotifications.remindUser()
    })
</script>
